import { useQuery } from "@apollo/client";

import { useEffect, useMemo } from "react";
import { GetS3FilesQuery, GetS3FilesQueryVariables } from "../../../API";
import { GET_S3_FILES } from "../../../common/operations/queries";

export interface S3File {
  filename: string;
  key: string;
  signedUrl: string;
  size?: number;
  lastModified?: string;
}

export interface AvailableDateWithCount {
  date: string;
  count: number;
}

export interface GetS3FilesResponse {
  deviceIdClean: string;
  nodeIdClean: string;
  bucketName: string;
  prefix: string;
  date: string;
  files: S3File[];
  totalFiles: number;
  availableDates?: string[];
  availableDatesWithCounts?: AvailableDateWithCount[];
}

export const useGetS3Files = (
  deviceIdClean: string,
  nodeIdClean: string,
  date: string,
  options?: {
    skip?: boolean;
    onCompleted?: (data: GetS3FilesResponse) => void;
    onError?: (error: Error) => void;
  }
) => {
  const { data, loading, error, refetch } = useQuery<
    GetS3FilesQuery,
    GetS3FilesQueryVariables
  >(GET_S3_FILES, {
    variables: {
      input: {
        deviceIdClean,
        nodeIdClean,
        date,
      },
    },
    skip: !deviceIdClean || !nodeIdClean || options?.skip,
    onError: options?.onError,
  });

  const parsedData = useMemo<GetS3FilesResponse | null>(() => {
    if (!data?.getS3Files) return null;

    try {
      const parsed = JSON.parse(data.getS3Files as string);

      if (parsed.statusCode === 200) {
        return parsed.body;
      } else {
        console.error("Lambda returned error status:", parsed);

        return null;
      }
    } catch (error) {
      console.error("Error parsing getS3Files response:", error);

      return null;
    }
  }, [data?.getS3Files]);

  useEffect(() => {
    if (parsedData && options?.onCompleted) {
      options.onCompleted(parsedData);
    }
  }, [parsedData, options?.onCompleted]);

  return {
    data: parsedData,
    loading,
    error,
    refetch,
    files: parsedData?.files || [],
    totalFiles: parsedData?.totalFiles || 0,
    availableDates: parsedData?.availableDates || [],
    availableDatesWithCounts: parsedData?.availableDatesWithCounts || [],
  };
};
